import React, { useCallback, useMemo, useState } from 'react';
import Heading from '../../../components/typography';
import FormSelect from '../../../components/dropdown/formSelect';
import { PageHeader } from '../../../constants/style/common.styled';
import { useSearchParticipants, useGetCsReasonCodes, useGetTradeOrderStatus } from '../../../hooks';
import useGetCompanies from '../../../hooks/get/useGetCompanies';
import { useWealthlaneCustomerApi } from '../../../hooks/useWealthlaneCustomerApi';
import { useWealthlaneAwardApi } from '../../../hooks/useWealthlaneAwardApi';
import { useWealthlaneAwardSettlementApi } from '../../../hooks/useWealthlaneAwardSettlement';
import type { ParticipantSearchResultDto } from '../../../services/wealthlane-customer-services/services/CustomerSuccessService';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import {
  ToolWrapper,
  ParticipantBar,
  ParticipantInfo,
  ActingOnBehalfBanner,
} from '../broker-trade-tool/brokerTradeTool.styled';
import DoneTradeSellShareSection from './DoneTradeSellShareSection';

function DoneTrade() {
  const { handleRequest: customerHandleRequest } = useWealthlaneCustomerApi();
  const { handleRequest: awardHandleRequest } = useWealthlaneAwardApi();
  const { handleRequest: settlementHandleRequest } = useWealthlaneAwardSettlementApi();

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantSearchResultDto | null>(
    null
  );

  const userAuthList = useSelector(selectAuthRoutesList);
  const policies = userAuthList?.auth?.grantedPolicies ?? {};
  const canFillDT = true; // !!policies['CustomerService.Customer.BrokerTradeTool.DoneTrade.Update'];

  // Submit API still requires reasonCode — use the first available code without showing the dropdown.
  const { data: reasonCodeOptions } = useGetCsReasonCodes(awardHandleRequest);
  const reasonCode = reasonCodeOptions?.[0]?.value ?? '';

  const { data: orderStatusOptions, isLoading: isOrderStatusLoading } =
    useGetTradeOrderStatus(settlementHandleRequest);
  const { data: companyOptions, isLoading: isCompaniesLoading } =
    useGetCompanies(customerHandleRequest);

  const { data: searchResults, isLoading: isParticipantsLoading } = useSearchParticipants(
    customerHandleRequest,
    selectedCompanyId || undefined,
    undefined,
    !!selectedCompanyId
  );

  const participantOptions = useMemo(() => {
    if (!searchResults) return [];
    return searchResults.map((p) => ({
      label: `${p.firstName} ${p.lastName} (${p.employeeId})`,
      value: p.participantId,
    }));
  }, [searchResults]);

  const handleCompanyChange = useCallback((value: string) => {
    setSelectedCompanyId(value ?? '');
    setSelectedParticipant(null);
  }, []);

  const handleParticipantChange = useCallback(
    (value: string) => {
      if (!value) {
        setSelectedParticipant(null);
        return;
      }
      const match = searchResults?.find((p) => p.participantId === value);
      if (match) {
        setSelectedParticipant(match);
      }
    },
    [searchResults]
  );

  return (
    <ToolWrapper>
      <PageHeader id='doneTradeHeader'>
        <Heading variant='h3' title='Done Trade' id='pageTitle' />
      </PageHeader>

      <div className='section-card'>
        <div className='section-title'>Participant Lookup</div>
        <ParticipantBar $columns={2}>
          <FormSelect
            type='text'
            name='company'
            id='doneTradeCompanySelect'
            label='Company'
            placeholder='Select company...'
            options={companyOptions}
            isLoading={isCompaniesLoading}
            values={selectedCompanyId}
            arrValue={selectedCompanyId}
            isFormik={false}
            onChange={(value: string) => {
              handleCompanyChange(value);
            }}
            required
          />

          <FormSelect
            type='text'
            name='participant'
            id='doneTradeParticipantSelect'
            label='Participant'
            placeholder={
              selectedCompanyId ? 'Search by name or employee ID...' : 'Select a company first'
            }
            options={participantOptions}
            values={selectedParticipant?.participantId ?? ''}
            arrValue={selectedParticipant?.participantId ?? ''}
            isFormik={false}
            disabled={!selectedCompanyId}
            isLoading={isParticipantsLoading}
            onChange={(value: string) => {
              handleParticipantChange(value);
            }}
            required
          />
        </ParticipantBar>

        {selectedParticipant && (
          <ParticipantInfo>
            <div className='info-item'>
              <span className='info-label'>Company</span>
              <span className='info-value'>{selectedParticipant.companyName}</span>
            </div>
            <div className='info-item'>
              <span className='info-label'>Ticker</span>
              <span className='info-value'>{selectedParticipant.ticker}</span>
            </div>
            <div className='info-item'>
              <span className='info-label'>Broker</span>
              <span className='info-value'>{selectedParticipant.brokerName || '-'}</span>
            </div>
            <div className='info-item'>
              <span className='info-label'>Account #</span>
              <span className='info-value'>{selectedParticipant.brokerAccountNumber || '-'}</span>
            </div>
          </ParticipantInfo>
        )}
      </div>

      {selectedParticipant && (
        <ActingOnBehalfBanner>
          <span className='material-symbols-outlined banner-icon'>person</span>
          <span>
            Acting on behalf of{' '}
            <span className='banner-name'>
              {selectedParticipant.firstName} {selectedParticipant.lastName}
            </span>{' '}
            ({selectedParticipant.employeeId}) — {selectedParticipant.companyName}
          </span>
        </ActingOnBehalfBanner>
      )}

      {selectedParticipant && (
        <div className='section-card'>
          <div className='section-title'>Sell Share</div>
          <DoneTradeSellShareSection
            participantId={selectedParticipant.participantId}
            companyId={selectedCompanyId || undefined}
            participant={selectedParticipant}
            reasonCode={reasonCode}
            awardHandleRequest={awardHandleRequest}
            orderStatusOptions={orderStatusOptions}
            isOrderStatusLoading={isOrderStatusLoading}
            canFillDT={canFillDT}
          />
        </div>
      )}
    </ToolWrapper>
  );
}

export default DoneTrade;
